import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import type { Merchant } from '@shared/types';

interface Props {
  merchant: Merchant;
  onUpdate: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function SquareConnectTab({ merchant, onUpdate }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const labelClass = 'block font-mono text-[10px] uppercase tracking-widest mb-1.5';
  const labelStyle: React.CSSProperties = { color: 'var(--dash-text-muted)' };

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/square/auth-url`, {
        headers: {
          'X-Bonespay-Key': merchant.api_key,
          Authorization: session?.access_token ? `Bearer ${session.access_token}` : '',
        },
      });

      const rawText = await res.text();
      let data: { success?: boolean; auth_url?: string; error?: string } = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        const snippet = rawText.trim().slice(0, 140);
        const statusLabel = res.status ? `HTTP ${res.status}` : 'Server error';
        throw new Error(
          `Square connection is not available right now (${statusLabel}).${
            snippet ? ` Server responded: "${snippet}"` : ''
          } Please contact support if this persists.`
        );
      }

      if (res.ok && data.success && data.auth_url) {
        window.location.href = data.auth_url;
        return;
      }

      setError(
        data.error ||
          `Failed to generate Square authorization URL (HTTP ${res.status}). The Square integration may not be configured on the server yet.`
      );
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect your Square account? You will need to re-authorize to accept Square payments again.')) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/square/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bonespay-Key': merchant.api_key,
          Authorization: session?.access_token ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const rawText = await res.text().catch(() => '');
        let data: { error?: string } = {};
        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch {
          const snippet = rawText.trim().slice(0, 140);
          throw new Error(
            `Disconnect failed (HTTP ${res.status}).${snippet ? ` Server responded: "${snippet}"` : ''}`
          );
        }
        throw new Error(data.error || `Disconnect failed (HTTP ${res.status})`);
      }
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Disconnect failed');
    } finally {
      setLoading(false);
    }
  };

  if (!merchant.square_onboarded || !merchant.square_merchant_id) {
    return (
      <div className="rounded p-8" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <div className="text-center">
          <div
            className="w-12 h-12 rounded flex items-center justify-center mx-auto mb-4 font-mono text-xl"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-hover-bg)', color: 'var(--dash-text-muted)' }}
          >
            &#8862;
          </div>
          <h3 className="font-sans text-base font-medium mb-2" style={{ color: 'var(--dash-text-primary)' }}>
            Connect Your Square Account
          </h3>
          <p className="font-sans text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--dash-text-muted)' }}>
            Accept card payments through Square. You will be redirected to Square to authorize BonesPay to process payments on your behalf.
          </p>
          {error && (
            <div className="mb-4 p-3 text-xs bg-red-500/10 border border-red-500/30 rounded max-w-md mx-auto text-red-400">
              {error}
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={loading}
            className="inline-flex items-center gap-2 text-white font-mono text-sm font-medium px-5 py-2.5 rounded transition-colors disabled:opacity-50 uppercase tracking-wider"
            style={{ background: '#006AFF' }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#0055CC'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#006AFF'; }}
          >
            {loading ? 'Connecting...' : 'Connect Square Account'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded p-5 space-y-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded">
          Connected
        </span>
        <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>Square Account</span>
      </div>

      <div>
        <label className={labelClass} style={labelStyle}>Square Merchant ID</label>
        <code
          className="block font-mono text-xs rounded px-3 py-2"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-secondary)' }}
        >
          {merchant.square_merchant_id}
        </code>
      </div>

      <div>
        <label className={labelClass} style={labelStyle}>Location ID</label>
        <code
          className="block font-mono text-xs rounded px-3 py-2"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-secondary)' }}
        >
          {merchant.square_location_id || 'Not set'}
        </code>
      </div>

      {merchant.square_token_expires_at && (
        <div>
          <label className={labelClass} style={labelStyle}>Access Token Expires</label>
          <p className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
            {new Date(merchant.square_token_expires_at).toLocaleDateString()} (refreshes automatically)
          </p>
        </div>
      )}

      <div className="pt-4" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
        <a
          href="https://squareup.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-mono text-xs transition-colors"
          style={{ color: 'var(--dash-text-muted)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
        >
          Open Square Dashboard
          <ExternalLink size={11} />
        </a>
      </div>

      <div>
        <button
          onClick={handleDisconnect}
          disabled={loading}
          className="text-xs font-mono text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 uppercase tracking-widest"
        >
          {loading ? 'Disconnecting...' : 'Disconnect Square'}
        </button>
      </div>

      {error && (
        <div className="p-3 text-xs bg-red-500/10 border border-red-500/30 rounded text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
